import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { SidebarService } from '../../core/services/sidebar.service';
import { ThemeService } from '../../core/services/theme.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

interface NavItem {
  icon: string;
  label: string;
  route: string;
  permission?: string;
}

interface NavGroup {
  label: string;
  icon: string;
  items: NavItem[];
  collapsed: boolean;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  navItems: NavItem[] = [];
  navGroups: NavGroup[] = [];
  isMobileOpen = false;
  isCollapsed = false;
  isLightTheme = false;
  readonly appVersion = environment.version;
  private destroy$ = new Subject<void>();

  constructor(
    private auth: AuthService,
    private router: Router,
    private sidebarService: SidebarService,
    private themeService: ThemeService,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe((theme) => {
      this.isLightTheme = theme === 'light';
    });

    this.sidebarService.open$.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      this.isMobileOpen = open;
    });

    this.sidebarService.collapsed$.pipe(takeUntil(this.destroy$)).subscribe((c) => {
      this.isCollapsed = c;
    });

    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.sidebarService.close();
      this.autoExpandActiveGroup();
    });

    this.navItems = [
      { icon: 'dashboard',  label: 'sidebar.dashboard', route: '/dashboard', permission: 'dashboard' },
      { icon: 'assessment', label: 'sidebar.reports',   route: '/reports',   permission: 'relatorios' },
    ];

    const financeiroRoutes  = ['/transactions', '/accounts-payable', '/accounts', '/credit-card', '/categories'];
    const clientesFaturasRoutes = ['/clients', '/invoices', '/operation-reports'];
    const rhRoutes          = ['/employees', '/service-providers', '/birthdays'];
    const grupoUsuariosRoutes   = ['/users', '/roles'];
    const sistemaRoutes         = ['/logs', '/test-runner', '/settings', '/sql-editor'];

    this.navGroups = [
      {
        label: 'sidebar.financeiro',
        icon: 'account_balance',
        collapsed: !financeiroRoutes.some(r => this.router.url.startsWith(r)),
        items: [
          { icon: 'swap_horiz', label: 'sidebar.transactions',   route: '/transactions',   permission: 'lancamentos' },
          { icon: 'payments',   label: 'sidebar.contas_a_pagar', route: '/accounts-payable', permission: 'contas-a-pagar' },
          { icon: 'account_balance_wallet', label: 'sidebar.accounts', route: '/accounts', permission: 'contas' },
          { icon: 'credit_card', label: 'sidebar.credit_card',   route: '/credit-card',   permission: 'cartao-credito' },
          { icon: 'category',   label: 'sidebar.categories',     route: '/categories',     permission: 'categorias' },
        ],
      },
      {
        label: 'sidebar.clientes_faturas',
        icon: 'business_center',
        collapsed: !clientesFaturasRoutes.some(r => this.router.url.startsWith(r)),
        items: [
          { icon: 'people_outline', label: 'sidebar.clients',  route: '/clients',  permission: 'clientes' },
          { icon: 'receipt_long',   label: 'sidebar.invoices', route: '/invoices', permission: 'faturas' },
          { icon: 'query_stats',    label: 'sidebar.operation_reports', route: '/operation-reports', permission: 'client:report' },
        ],
      },
      {
        label: 'sidebar.rh',
        icon: 'groups',
        collapsed: !rhRoutes.some(r => this.router.url.startsWith(r)),
        items: [
          { icon: 'badge',      label: 'sidebar.funcionarios',  route: '/employees',        permission: 'funcionarios' },
          { icon: 'handshake',  label: 'sidebar.prestadores',  route: '/service-providers', permission: 'service-providers' },
          { icon: 'cake',       label: 'sidebar.aniversariantes', route: '/birthdays',      permission: 'aniversariantes' },
        ],
      },
      {
        label: 'sidebar.grupo_usuarios',
        icon: 'manage_accounts',
        collapsed: !grupoUsuariosRoutes.some(r => this.router.url.startsWith(r)),
        items: [
          { icon: 'people',               label: 'sidebar.users', route: '/users', permission: 'usuarios' },
          { icon: 'admin_panel_settings', label: 'sidebar.roles', route: '/roles', permission: 'configuracoes' },
        ],
      },
      {
        label: 'sidebar.sistema',
        icon: 'tune',
        collapsed: !sistemaRoutes.some(r => this.router.url.startsWith(r)),
        items: [
          { icon: 'manage_history', label: 'sidebar.logs',        route: '/logs',        permission: 'configuracoes' },
          { icon: 'bug_report',     label: 'sidebar.test_runner', route: '/test-runner', permission: 'configuracoes' },
          { icon: 'terminal',       label: 'SQL Editor',          route: '/sql-editor',  permission: 'sql_editor' },
          { icon: 'settings',       label: 'sidebar.settings',    route: '/settings' },
        ],
      },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get allItems(): NavItem[] {
    return [
      ...this.navItems,
      ...this.navGroups.flatMap(g => g.items),
    ];
  }

  isVisible(item: NavItem): boolean {
    if (!item.permission) return true;
    return this.auth.hasPermission(item.permission);
  }

  isGroupVisible(group: NavGroup): boolean {
    return group.items.some(item => this.isVisible(item));
  }

  get hasVisibleNavItems(): boolean {
    return this.navItems.some(item => this.isVisible(item));
  }

  isActive(route: string): boolean {
    const url = this.router.url.split('?')[0];
    return url === route || url.startsWith(route + '/');
  }

  toggleGroup(group: NavGroup): void {
    group.collapsed = !group.collapsed;
  }

  toggleCollapse(): void {
    this.sidebarService.toggleCollapsed();
  }

  private autoExpandActiveGroup(): void {
    for (const group of this.navGroups) {
      if (group.items.some(item => this.router.url.startsWith(item.route))) {
        group.collapsed = false;
      }
    }
  }
}
